import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { useQuery, useReactiveVar } from '@apollo/client';
import { OrderConfirmationIframe } from '@chegg/order-confirmation';
import { Component, ConversionType, CTA as Cta, DropInPaymentsCommandNames, ElementType, fireLogEvent, fireStatusUpdate, LinkText, Namespace, ProductType, SpecType, StatusTypes, SubType, UserActions, getBillingCycleContentKey, getPriceQuoteItems, sendRioEvent, updateRioClientExperiment, getRecurlyPreviewPurchase, getRecurlyCart, formatCurrency } from '@chegg/commerce-utils';
import { OptimizelyWebPageName, useManualOptimizelyWebActivation } from '@chegg/optimizely-sdk';
import React, { useEffect, useState } from 'react';
import { useCartCheckoutInitializeState } from '../../hooks/useCartCheckoutInitializeState';
import { useCartCheckoutRioEvents } from '../../hooks/useCartCheckoutRioEvents';
import { initApolloClient, setApolloState } from '../../lib/ApolloSchema/apolloUtils';
import { reactiveAltProductData, reactiveCartData } from '../../lib/ApolloSchema/reactiveVars';
import { GET_APOLLO_STATE } from '../../lib/Graphql/getApolloState';
import { DIP_CALLBACK_CONTEXT } from '../../modules/DropInPayments/constants';
import { getPrice, getPriceWithoutDiscount } from '../../utils/TransformUtils';
import getPriceQuoteItemData from '../../utils/getPriceQuoteItemData';
import onError from '../../utils/onError';
import { CART_CHECKOUT_TESTID, CSP_PACKAGE_INDEX, CS_PACKAGE_INDEX } from './constants';
import { getIsEligibleForPithyValueProps, onSecondaryClickHandler, showSecondaryCTA } from './helpers';
import DefaultCheckoutLayout from './internals/DefaultCheckoutLayout';
import PlanSelectionLayout from './internals/PlanSelectionLayout';
import { CTAWrapper, CartCheckoutWrapper } from './styled';
import { getClient } from '@chegg-web-tools/apollo-utils';

/* istanbul ignore next */
var getSelectedPrice = function getSelectedPrice(_ref) {
  var selectedProductVariationPrice = _ref.selectedProductVariationPrice,
    selectedProduct = _ref.selectedProduct,
    itemData = _ref.itemData,
    priceWithoutDiscount = _ref.priceWithoutDiscount,
    altPriceWithoutDiscount = _ref.altPriceWithoutDiscount,
    isRecurly = _ref.isRecurly,
    recurlyCart = _ref.recurlyCart,
    calculatedPrice = _ref.calculatedPrice;
  if (isRecurly && recurlyCart) {
    return calculatedPrice;
  }
  if (selectedProductVariationPrice) {
    return selectedProductVariationPrice;
  }
  if (selectedProduct.productType === itemData[0].productType) {
    return priceWithoutDiscount;
  }
  return altPriceWithoutDiscount;
};
var CartCheckout = function CartCheckout(_ref2) {
  var _priceQuoteItems$find, _content$packages, _apolloState$orderDat;
  var action = _ref2.action,
    authorization = _ref2.authorization,
    content = _ref2.content,
    currencyCode = _ref2.currencyCode,
    country = _ref2.country,
    displayServerErrors = _ref2.displayServerErrors,
    environment = _ref2.environment,
    externalId = _ref2.externalId,
    headingLevel = _ref2.headingLevel,
    hostAnnotations = _ref2.hostAnnotations,
    isRioInitialized = _ref2.isRioInitialized,
    isVpnDetected = _ref2.isVpnDetected,
    itemData = _ref2.itemData,
    namespace = _ref2.namespace,
    optimizelyClient = _ref2.optimizelyClient,
    price = _ref2.price,
    priceTotalWithTaxCurrency = _ref2.priceTotalWithTaxCurrency,
    priceWithoutCurrency = _ref2.priceWithoutCurrency,
    rioConfig = _ref2.rioConfig,
    referrer = _ref2.referrer,
    reviewStepEnabled = _ref2.reviewStepEnabled,
    taxInfo = _ref2.taxInfo,
    testOrderState = _ref2.testOrderState,
    userUuid = _ref2.userUuid,
    useForter = _ref2.useForter,
    withCoupon = _ref2.withCoupon,
    allowPlanSelection = _ref2.allowPlanSelection,
    eligibleForCSPUpgrade = _ref2.eligibleForCSPUpgrade,
    experiment = _ref2.experiment,
    products = _ref2.products,
    isMultimonthResubscribe = _ref2.isMultimonthResubscribe,
    bypassOrderConfirmIframe = _ref2.bypassOrderConfirmIframe,
    couponCode = _ref2.couponCode,
    useAutoAppliedCoupons = _ref2.useAutoAppliedCoupons,
    sendRioViewEvents = _ref2.sendRioViewEvents;
  var isRecurly = itemData[0].catalogItemSource === 'RECURLY';
  var client = initApolloClient();
  var _useQuery = useQuery(GET_APOLLO_STATE, {
      client: client
    }),
    apolloState = _useQuery.data;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    recurlyCart = _useState2[0],
    setRecurlyCart = _useState2[1];
  var recurlyTaxInfo = recurlyCart !== null && recurlyCart !== void 0 && recurlyCart.orderSummary.total ? {
    totalWithTax: recurlyCart.orderSummary.total,
    tax: recurlyCart.orderSummary.tax,
    priceTotalWithTaxCurrency: recurlyCart.orderSummary.totalWithCurrencyFormat,
    taxTotalWithCurrency: recurlyCart.orderSummary.taxWithCurrencyFormat,
    taxDetails: []
  } : undefined;
  var eligibleForPithyValueProps = getIsEligibleForPithyValueProps(country, action, itemData[0].productType);
  useManualOptimizelyWebActivation({
    skip: !eligibleForPithyValueProps,
    pageName: OptimizelyWebPageName.S2_PLUS_RESUB_MODAL
  });

  // Update Rio Client with newly allocated experiment in opt-user-profile cookie
  updateRioClientExperiment(namespace);

  // Product that OneClick was initialized with will be preselected
  // (CS for upgradeToCSP, CSP for new UAE users)
  var priceQuoteItems = getPriceQuoteItems(products[0]);
  var initialSelectedProduct = (_priceQuoteItems$find = priceQuoteItems.find(function (_ref3) {
    var optionPreference = _ref3.optionPreference;
    return optionPreference === null || optionPreference === void 0 ? void 0 : optionPreference.defaultItem;
  })) !== null && _priceQuoteItems$find !== void 0 ? _priceQuoteItems$find : priceQuoteItems[0];
  var _useState3 = useState(isMultimonthResubscribe ? {
      productType: ProductType.BUNDLE,
      id: initialSelectedProduct.itemDetails.id,
      billingPeriod: initialSelectedProduct.billingPeriod
    } : {
      productType: itemData[0].productType,
      id: itemData[0].catalogItemId
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedProduct = _useState4[0],
    setSelectedProduct = _useState4[1];

  /* istanbul ignore next */
  var selectedProductVariation = isMultimonthResubscribe ? priceQuoteItems.find(function (_ref4) {
    var id = _ref4.itemDetails.id;
    return id === selectedProduct.id;
  }) : undefined;
  var selectedProductVariationPrice = selectedProductVariation && selectedProductVariation.totalBeforeTax;
  var billingCycleContentKey = selectedProduct.billingPeriod && getBillingCycleContentKey(selectedProduct.billingPeriod);
  var cartData = useReactiveVar(reactiveCartData);
  var selectedPackageIndex = selectedProduct.productType === 'BUNDLE' ? CSP_PACKAGE_INDEX : CS_PACKAGE_INDEX;
  var cta = content.cta;
  var _ref5 = ((_content$packages = content.packages) === null || _content$packages === void 0 ? void 0 : _content$packages[selectedPackageIndex]) || content,
    tax = _ref5.tax,
    termsAndConditions = _ref5.termsAndConditions;
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showOnlyTnc = _useState6[0],
    setShowOnlyTnc = _useState6[1];
  var cspProduct = isMultimonthResubscribe ? products[0] : products[1];
  var csProduct = isMultimonthResubscribe ? null : products[0];
  var _useCartCheckoutIniti = useCartCheckoutInitializeState({
      apolloState: apolloState,
      itemData: isMultimonthResubscribe && selectedProductVariation ? getPriceQuoteItemData(selectedProductVariation, cspProduct) : itemData,
      hostAnnotations: hostAnnotations,
      reviewStepEnabled: reviewStepEnabled,
      allowPlanSelection: allowPlanSelection,
      couponCode: couponCode,
      useAutoAppliedCoupons: useAutoAppliedCoupons,
      isRecurly: isRecurly
    }),
    dipCommands = _useCartCheckoutIniti.dipCommands,
    setDipCommands = _useCartCheckoutIniti.setDipCommands,
    isMobile = _useCartCheckoutIniti.isMobile,
    isReviewStep = _useCartCheckoutIniti.isReviewStep;
  var _useReactiveVar = useReactiveVar(reactiveAltProductData),
    altPrice = _useReactiveVar.altPrice,
    altPriceTotalWithTaxCurrency = _useReactiveVar.altPriceTotalWithTaxCurrency,
    altDiscountedPrice = _useReactiveVar.altDiscountedPrice,
    altItemData = _useReactiveVar.altItemData;

  /* istanbul ignore next */
  var selectProduct = function selectProduct(_ref6) {
    var productType = _ref6.productType,
      itemData = _ref6.itemData,
      taxInfo = _ref6.taxInfo,
      orderSummaryContent = _ref6.orderSummaryContent,
      billingPeriod = _ref6.billingPeriod;
    /* istanbul ignore next */ // tested in child component
    var cartItems = productType === ProductType.BUNDLE ? altItemData : itemData;
    if (isRecurly && csProduct) {
      var _externalId = productType === ProductType.BUNDLE ? cspProduct.externalId : csProduct.externalId;
      void updateRecurlyCart(_externalId, cartItems);
    } else {
      setDipCommands([{
        commandName: DropInPaymentsCommandNames.REPLACE_ITEM,
        data: {
          itemData: cartItems,
          taxInfo: taxInfo,
          orderSummaryContent: orderSummaryContent
        }
      }]);
    }
    setSelectedProduct({
      productType: productType,
      id: itemData[0].catalogItemId,
      billingPeriod: billingPeriod
    });
  };
  var updateRecurlyCart = /*#__PURE__*/function () {
    var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(externalId, cartItems) {
      var _previewPurchase$tota, _previewPurchase$tota2;
      var previewPurchase, cartResponse, discountedPrice, formattedDiscountedPrice, altProductData;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getRecurlyPreviewPurchase({
              externalId: externalId,
              currency: currencyCode,
              countryCode: country,
              uuid: userUuid !== null && userUuid !== void 0 ? userUuid : '',
              couponCode: couponCode,
              apolloClient: getClient()
            });
          case 2:
            previewPurchase = _context.sent;
            cartResponse = getRecurlyCart({
              externalId: externalId,
              cartItems: cartItems,
              previewPurchase: previewPurchase,
              currency: currencyCode
            });
            setRecurlyCart(cartResponse);

            // In previewPurchase response, totalAmount = (basePrice + tax) - discount. BE does not return (basePrice - discount).
            discountedPrice = cartResponse !== null && cartResponse !== void 0 && cartResponse.orderSummary.discount ? cartResponse.orderSummary.subtotal - cartResponse.orderSummary.discount : 0;
            formattedDiscountedPrice = formatCurrency(discountedPrice, currencyCode); // Calculate and store CSP discounted price
            if (((_previewPurchase$tota = previewPurchase === null || previewPurchase === void 0 ? void 0 : previewPurchase.totalDiscount) !== null && _previewPurchase$tota !== void 0 ? _previewPurchase$tota : 0) > 0 && (cartItems === null || cartItems === void 0 ? void 0 : cartItems[0].productType) === ProductType.BUNDLE) {
              /* istanbul ignore next */
              altProductData = reactiveAltProductData();
              /* istanbul ignore next */
              if (altProductData.altItemData.length > 0) {
                reactiveAltProductData(_objectSpread(_objectSpread({}, altProductData), {}, {
                  altDiscountedPrice: formattedDiscountedPrice !== null && formattedDiscountedPrice !== void 0 ? formattedDiscountedPrice : undefined,
                  isAltDiscountedPriceFetched: true
                }));
              }
            }
            // Calculate and store CS discounted price
            if (((_previewPurchase$tota2 = previewPurchase === null || previewPurchase === void 0 ? void 0 : previewPurchase.totalDiscount) !== null && _previewPurchase$tota2 !== void 0 ? _previewPurchase$tota2 : 0) > 0 && (cartItems === null || cartItems === void 0 ? void 0 : cartItems[0].productType) === ProductType.CHEGG_STUDY) {
              /* istanbul ignore next */
              reactiveCartData({
                discountedPrice: formattedDiscountedPrice !== null && formattedDiscountedPrice !== void 0 ? formattedDiscountedPrice : undefined,
                totalWithCurrencyFormat: (cartResponse === null || cartResponse === void 0 ? void 0 : cartResponse.orderSummary.totalWithCurrencyFormat) || undefined
              });
            }
          case 9:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function updateRecurlyCart(_x, _x2) {
      return _ref7.apply(this, arguments);
    };
  }();
  useEffect(function () {
    if (isRecurly) {
      void updateRecurlyCart(externalId, itemData);
      /* istanbul ignore next */
      if (allowPlanSelection) {
        void updateRecurlyCart(cspProduct.externalId, altItemData);
      }
    }
  }, [isRecurly, couponCode]);
  useEffect(function () {
    // Scroll to top of modal once opened
    if (isMobile || reviewStepEnabled) {
      var _document$getElementB;
      var headerElement = (_document$getElementB = document.getElementById('one-click-checkout-modal')) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.querySelector('h1');
      setTimeout(function () {
        headerElement === null || headerElement === void 0 || headerElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [isMobile, reviewStepEnabled]);
  useCartCheckoutRioEvents({
    action: action,
    currencyCode: currencyCode,
    externalId: externalId,
    isReviewStep: isReviewStep,
    isRioInitialized: isRioInitialized,
    namespace: namespace,
    priceWithoutCurrency: priceWithoutCurrency,
    selectedProductVariationPrice: selectedProductVariationPrice,
    reviewStepEnabled: reviewStepEnabled,
    rioConfig: rioConfig,
    sendRioViewEvents: sendRioViewEvents
  });
  var isCTALoading = apolloState === null || apolloState === void 0 ? void 0 : apolloState.uiData.isCTALoading;
  var isCTADisabled = apolloState && (apolloState.uiData.isCTADisabled || apolloState.uiData.showInitializeError);
  var disableCtaFooter = isMobile && !!(apolloState !== null && apolloState !== void 0 && apolloState.uiData.showOnlyPM);

  /* istanbul ignore next */
  var _ref8 = (_apolloState$orderDat = apolloState === null || apolloState === void 0 ? void 0 : apolloState.orderData) !== null && _apolloState$orderDat !== void 0 ? _apolloState$orderDat : {},
    token = _ref8.token,
    cartId = _ref8.cartId;

  /* istanbul ignore next */
  var onClickCtaHandler = function onClickCtaHandler() {
    setDipCommands([{
      commandName: reviewStepEnabled && isReviewStep ? DropInPaymentsCommandNames.PLACE_ORDER : DropInPaymentsCommandNames.REVIEW_VIEW
    }]);
    var linkText = '';
    if (reviewStepEnabled && !isReviewStep) {
      linkText = LinkText.REVIEW_ORDER;
    } else if (action === UserActions.JOIN) {
      linkText = LinkText.SUBSCRIBE;
    } else if (action === UserActions.RESUBSCRIBE) {
      linkText = LinkText.RESUBSCRIBE;
    }
    linkText && sendRioEvent({
      info: linkText,
      specType: SpecType.INTERACTION,
      subType: SubType.CLICK,
      elementType: ElementType.BUTTON,
      callback: fireLogEvent,
      namespace: namespace
    });
    setApolloState({
      uiData: {
        errorMessage: '',
        isCTADisabled: true,
        isCTALoading: true
      }
    });
  };
  var ctaContent = disableCtaFooter ? /*#__PURE__*/React.createElement(React.Fragment, null) : /*#__PURE__*/React.createElement(CTAWrapper, null, /*#__PURE__*/React.createElement(Cta, {
    onClick: onClickCtaHandler,
    content: cta,
    onError: onError,
    disabled: isCTADisabled,
    loading: isCTALoading,
    namespace: namespace,
    isReview: reviewStepEnabled && !isReviewStep,
    hideLockIcon: true,
    isUpgrade: action === UserActions.UPGRADE
  }), showSecondaryCTA(country) && /*#__PURE__*/React.createElement(Cta, {
    onClick: onSecondaryClickHandler,
    content: cta,
    disabled: isCTADisabled,
    onError: onError,
    isSecondary: true,
    namespace: namespace
  }));

  /* istanbul ignore next */
  var calculatedPrice = getPrice({
    country: country,
    price: price,
    discountedPrice: cartData.discountedPrice,
    priceTotalWithTaxCurrency: priceTotalWithTaxCurrency || cartData.totalWithCurrencyFormat || (recurlyTaxInfo === null || recurlyTaxInfo === void 0 ? void 0 : recurlyTaxInfo.priceTotalWithTaxCurrency)
  });
  var priceWithoutDiscount = getPriceWithoutDiscount({
    country: country,
    price: price,
    priceTotalWithTaxCurrency: priceTotalWithTaxCurrency || cartData.totalWithCurrencyFormat
  });
  var altPriceWithoutDiscount = getPriceWithoutDiscount({
    country: country,
    price: altPrice,
    priceTotalWithTaxCurrency: altPriceTotalWithTaxCurrency
  });
  var selectedPrice = getSelectedPrice({
    selectedProductVariationPrice: selectedProductVariationPrice,
    selectedProduct: selectedProduct,
    itemData: itemData,
    priceWithoutDiscount: priceWithoutDiscount,
    altPriceWithoutDiscount: altPriceWithoutDiscount,
    isRecurly: isRecurly,
    recurlyCart: recurlyCart,
    calculatedPrice: calculatedPrice
  });
  var selectedPriceWithDiscount = selectedProductVariationPrice ? cartData.discountedPrice : selectedProduct.productType === itemData[0].productType ? cartData.discountedPrice : altDiscountedPrice;
  var tncProps = {
    authorization: authorization,
    price: selectedPrice,
    fireLogEvent: fireLogEvent,
    isOpen: showOnlyTnc,
    namespace: namespace,
    onError: onError,
    setShowOnlyTnc: setShowOnlyTnc,
    termsAndConditions: termsAndConditions,
    ctaLabel: cta === null || cta === void 0 ? void 0 : cta.cta,
    billingCycleContentKey: billingCycleContentKey,
    isCouponApplied: !!selectedPriceWithDiscount,
    priceWithDiscount: selectedPriceWithDiscount
  };
  var isPurchaseComplete = token || cartId || (testOrderState === null || testOrderState === void 0 ? void 0 : testOrderState.orderData.token);
  var productFamily = itemData[0].productType;
  var orderSuccessStatusUpdate = [{
    command: DropInPaymentsCommandNames.PLACE_ORDER,
    status: StatusTypes.SUCCESS,
    payload: {
      component: Component.CART_CHECKOUT,
      context: DIP_CALLBACK_CONTEXT,
      data: {
        isNewSub: action === UserActions.JOIN,
        token: token,
        productFamily: productFamily,
        productId: externalId
      }
    }
  }];
  useEffect(function () {
    if (isPurchaseComplete && bypassOrderConfirmIframe) {
      fireStatusUpdate(orderSuccessStatusUpdate, Namespace.ONE_CLICK);
    }
  }, [isPurchaseComplete, bypassOrderConfirmIframe]);

  /* istanbul ignore next */
  var onIframeLoaded = function onIframeLoaded(_ref9) {
    var _payload$data;
    var payload = _ref9.payload;
    if ((_payload$data = payload.data) !== null && _payload$data !== void 0 && _payload$data.ocpIframeLoaded) {
      setTimeout(function () {
        fireStatusUpdate(orderSuccessStatusUpdate, Namespace.ONE_CLICK);
      }, 3000);
    }
  };
  var hasNeedHelpButton = country !== 'US';
  var CartCheckoutLayout = allowPlanSelection ? PlanSelectionLayout : DefaultCheckoutLayout;

  /* istanbul ignore next */
  return /*#__PURE__*/React.createElement(CartCheckoutWrapper, {
    "data-test": CART_CHECKOUT_TESTID
  }, /*#__PURE__*/React.createElement(CartCheckoutLayout, {
    authorization: authorization,
    calculatedPrice: calculatedPrice,
    content: content,
    ctaContent: ctaContent,
    displayServerErrors: displayServerErrors,
    headingLevel: headingLevel,
    isMobile: isMobile,
    isReviewStep: isReviewStep,
    isVpnDetected: isVpnDetected,
    reviewStepEnabled: reviewStepEnabled,
    selectProduct: selectProduct,
    selectedProduct: selectedProduct,
    tax: tax,
    tncProps: tncProps,
    uiData: apolloState === null || apolloState === void 0 ? void 0 : apolloState.uiData,
    itemData: itemData,
    taxInfo: isRecurly ? recurlyTaxInfo : taxInfo,
    action: action,
    environment: environment,
    optimizelyClient: optimizelyClient,
    referrer: referrer,
    userUuid: userUuid,
    useForter: useForter,
    withCoupon: withCoupon,
    country: country,
    dipCommands: dipCommands,
    externalId: externalId,
    isCTALoading: isCTALoading,
    eligibleForCSPUpgrade: eligibleForCSPUpgrade,
    experiment: experiment,
    products: products,
    isMultimonthResubscribe: isMultimonthResubscribe,
    hasNeedHelpButton: hasNeedHelpButton,
    couponCode: couponCode,
    priceWithDiscount: selectedPriceWithDiscount,
    price: price,
    recurlyCart: recurlyCart,
    isRecurly: isRecurly
  }), /* istanbul ignore next */ // test already added
  !bypassOrderConfirmIframe && (token || cartId || (testOrderState === null || testOrderState === void 0 ? void 0 : testOrderState.orderData.token)) && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(OrderConfirmationIframe, {
    token: token || cartId || '',
    cheggBaseUrl: authorization.cheggBaseUrl || '',
    conversionType: ConversionType.SINGLE_ITEM_PURCHASE,
    productFamily: productFamily,
    productId: externalId,
    blockDDRW: true,
    callback: onIframeLoaded
  })));
};
export default CartCheckout;


//////////////////
// WEBPACK FOOTER
// ./node_modules/@chegg/writing-checkout/node_modules/@chegg/one-click/dist/esm/views/CartCheckout/index.js
// module id = 4194
// module chunks = 11